import React from "react";
import * as XLSX from "xlsx";
import { CardBody, Container, Modal, Popover } from "reactstrap";
import { isMobile } from "react-device-detect";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";

import { MobileDateRangePickerWrapper } from "@components/Common/MobileDateRangePickerWrapper";
import DateRangePickerWrapper from "@components/Common/DateRangePickerWrapper";
import { DataGrid, GridColDef, GridRenderCellParams } from "@mui/x-data-grid";
import HeaderComponent from "@components/VerticalLayout/HeaderComponent";
import { StyledPopoverBody } from "@pages/connecs/StyledPopoverBody";
import BreadcrumbOptions from "@pages/Reporting/BreadcrumbOptions";
import {
    downloadStoreReportCsv,
    getAdvancedPeriodStringParam,
} from "@helpers/general";
import PageTitle from "@components/Common/PageTitle";

import { CustomFooter } from "@pages/connecs/CustomFooter";
import { Box, Tooltip } from "@mui/material";

import { TransactionsReportingModal } from "./TransactionsReportingModal";
import uploadIcon from "../../assets/images/uploadIcon.svg";
import exportIcon from "../../assets/images/exportIcon.svg";
import NoReportOverlay from "./NoReportOverlay";
import { RootState } from "@store/index";

function SoftaveraTransactionsOverview() {
    const { t } = useTranslation();
    const mainColor: string = useSelector(
        (State: RootState): string => State.colorSlice.dominantColor
    );
    const { periodSlice, advancedDate } = useSelector((state: any) => state);
    const [isTransactionsModalOpened, setIsTransactionsModalOpened] =
        React.useState<boolean>(false);
    const [rowData] = React.useState<string>("");
    const [, setTransactionData] = React.useState<any>();
    const uuidEntity = localStorage.getItem("Uid_Entitie_User");
    const [currentPage, setCurrentPage] = React.useState(0);
    const [rows, setRows] = React.useState<any>([]);
    const [isPopoverOpened, setIsPopoverOpened] = React.useState(false);
    const [isModalOpened, setIsModalOpened] = React.useState<boolean>(false);
    const [columns, setColumns] = React.useState<GridColDef[]>([]);
    const [pageSize, setPageSize] = React.useState(10);
    const totalPages = Math.ceil(rows.length / pageSize);
    const handlePageChange = (newPage: number) => {
        if (newPage >= 0 && newPage < totalPages) {
            setCurrentPage(newPage);
        }
    };

    const toggleOpenedPopover = React.useCallback(() => {
        setIsPopoverOpened((prevState) => !prevState);
    }, []);

    const toggleOpeningModal = React.useCallback(() => {
        setIsModalOpened((prevState) => !prevState);
    }, []);

    const handleToggleOpeningModal = React.useCallback(() => {
        toggleOpeningModal();
    }, [toggleOpeningModal]);

    const handleToggleOpenedPopover = React.useCallback(() => {
        toggleOpenedPopover();
    }, [toggleOpenedPopover]);

    async function getTransactionsReporting() {
        const filterPeriod = getAdvancedPeriodStringParam(
            periodSlice,
            advancedDate
        );
        const encodedFilterPeriod = encodeURIComponent(filterPeriod);

        const response = await fetch(
            `${process.env.REACT_APP_ADMIN_ACCESS}/getTransactionsReporting?inputDate=${encodedFilterPeriod}&uuidEntity=${uuidEntity}&merchentAccount=${process.env.REACT_APP_MERCHENT_POS}`
        );

        if (!response.ok) {
            throw new Error(`Error fetching data: ${response.status}`);
        }

        const rawData = await response.json();

        const mappedData = rawData.map((item: any, index: number) => ({
            id: index,
            date: item.date,
            totalCmd: ` ${item.totalCmd} ${item.currency}`,
            totalFrais: `${item.totalFrais} ${item.currency}`,
            totalRenverser: `${item.totalRenverser} ${item.currency}`,
            totalFraisFranchise: item.totalFraisFranchise,
            totalFraisSoftavera: item.totalFraisSoftavera,
            versementDate: item.versementDate,
            nombresTransactions: item.nombresTransactions,
            storereportname: item.storereportname,
            merchentAccount: item.merchentAccount,
            fraisInterchange: item.frais_interchange,
            currency: item.currency,
        }));

        setTransactionData(mappedData);

        const hasInterchange = mappedData.some(
            (item: any) => Number(item.fraisInterchange) > 0
        );

        const dynamicColumns: GridColDef[] = [
            {
                field: "date",
                headerName: "Date",

                width: 230,
                editable: false,
            },
            {
                field: "nombresTransactions",
                headerName: "Nombre des Transactions",

                width: 230,
                editable: false,
            },
            {
                field: "totalCmd",
                headerName: "Total des Transactions",

                width: 230,
                editable: false,
            },
            {
                field: "totalFrais",
                headerName: "Total des frais",

                width: 220,
                editable: false,
            },
            // {
            //     field: "totalFraisFranchise",
            //     headerName: "Frais Franchise",

            //     width: 220,
            //     editable: false,
            // },
            // {
            //     field: "totalFraisSoftavera",
            //     headerName: "Frais Softavera",

            //     width: 220,
            //     editable: false,
            // },
            {
                field: "totalRenverser",
                headerName: "Total à verser",

                width: 250,
                editable: false,
            },
            {
                field: "versementDate",
                headerName: "Date de Versement",

                width: 250,
                editable: false,
            },
            {
                field: "actions",
                flex: 1,
                headerName: "Actions",
                headerClassName: "custom-header",
                renderCell: (params: GridRenderCellParams) => (
                    <div
                        style={{
                            display: "flex",
                            gap: "20px",
                            alignItems: "center",
                        }}
                    >
                        <Tooltip title="Télécharger transactions" arrow>
                            <button
                                type="button"
                                onClick={async () => {
                                    await downloadStoreReportCsv(
                                        params.row.storereportname,
                                        params.row.merchentAccount
                                    );
                                }}
                                aria-label="Download CSV"
                                style={{
                                    background: "none",
                                    border: "none",
                                    padding: 0,
                                    cursor: "pointer",
                                }}
                            >
                                <img
                                    src={uploadIcon}
                                    height={30}
                                    width={30}
                                    alt="Download CSV"
                                    style={{
                                        backgroundColor: "transparent",
                                        mixBlendMode: "multiply",
                                    }}
                                />
                            </button>
                        </Tooltip>
                    </div>
                ),
                minWidth: 100,
                sortable: false,
            },
        ];

        if (hasInterchange) {
            dynamicColumns.splice(4, 0, {
                field: "fraisInterchange",
                headerName: "Frais interchange ++",
                width: 220,
                editable: false,
                valueGetter: (params) => {
                    return `${params.row.fraisInterchange} ${params.row.currency}`;
                },
            });
        }

        setColumns(dynamicColumns);

        return mappedData;
    }

    const handlePageSizeChange = (newPageSize: number) => {
        setPageSize(newPageSize);
        setCurrentPage(0);
    };

    async function downloadAll() {
        const storeName = rows[0].storereportname;
        const parts = storeName.split("_");
        const fileName = parts.slice(1).join("_");
        const formattedData = rows.map((row: any) => {
            const formattedRow: any = {
                Date: row.date,
                "Nombre des Transactions": row.nombresTransactions,
                "Total des Transactions": row.totalCmd,
                "Total des frais": row.totalFrais,
                "Total à verser": row.totalRenverser,
                "Date de Versement": row.versementDate,
            };

            if (row.fraisInterchange > 0) {
                formattedRow["Frais interchange ++"] = row.fraisInterchange;
            }

            return formattedRow;
        });

        const worksheet = XLSX.utils.json_to_sheet(formattedData);
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, "Rapport");
        XLSX.writeFile(workbook, `${fileName}_transactions.xlsx`);
    }
    React.useEffect(() => {
        const loadRows = async () => {
            const fetchedRows: any = await getTransactionsReporting();
            setRows(fetchedRows);
        };

        loadRows();
        // eslint-disable-next-line
    }, [uuidEntity, periodSlice, advancedDate]);

    return (
        <React.Fragment>
            <div>
                <PageTitle title="Reporting SoftaveraPay" />
                <div className="">
                    <BreadcrumbOptions
                        currentLink={[
                            {
                                item: t("SOFTAVERAPAY"),
                                link: `${process.env.REACT_APP_PORTAIL_API}`,
                            },
                            {
                                item: t(`${"Reporting"}`),
                                link: `${"Reporting"}`,
                            },
                            {
                                item: t(`${"Reporting journalier POS"}`),
                                link: `${"Reporting journalier POS"}`,
                            },
                        ]}
                    />
                    <Container fluid>
                        <HeaderComponent
                            title={t(
                                `The daily report shows the total transactions per day, the total fees, as well as the total after fee deductions.`
                            )}
                        />
                        <CardBody
                            style={{
                                padding: "1px",
                                backgroundColor: "white",
                                marginTop: "10px",
                            }}
                        >
                            <div>
                                <div className="transaction-overview">
                                    {/* <h2>Reporting journalier overview</h2> */}
                                    <div className="filter-buttons">
                                        <button
                                            id="date-range-picker-popover"
                                            type="button"
                                            className="filter-button active"
                                            onClick={handleToggleOpenedPopover}
                                        >
                                            {t(`${periodSlice.date}`)}
                                        </button>
                                        {isPopoverOpened &&
                                            (isMobile === false ? (
                                                <Popover
                                                    placement="top"
                                                    isOpen={isPopoverOpened}
                                                    target="date-range-picker-popover"
                                                    toggle={toggleOpenedPopover}
                                                    trigger="legacy"
                                                >
                                                    <StyledPopoverBody>
                                                        <DateRangePickerWrapper
                                                            toggleOpenedPopover={
                                                                toggleOpenedPopover
                                                            }
                                                            isAdvancedReporting
                                                        />
                                                    </StyledPopoverBody>
                                                </Popover>
                                            ) : (
                                                <Modal
                                                    isOpen={isModalOpened}
                                                    toggle={
                                                        handleToggleOpeningModal
                                                    }
                                                    className="defined-range-modal__clz"
                                                    centered
                                                    fade={false}
                                                >
                                                    <MobileDateRangePickerWrapper
                                                        toggleOpeningModal={
                                                            handleToggleOpeningModal
                                                        }
                                                    />
                                                </Modal>
                                            ))}

                                        <button
                                            disabled={!(rows.length > 0)}
                                            style={{
                                                cursor:
                                                    rows.length > 0
                                                        ? "auto"
                                                        : "none",
                                                opacity:
                                                    rows.length > 0
                                                        ? "1"
                                                        : "0.5",
                                                marginLeft: "81%",
                                                width: "125px",
                                                display: "flex",
                                                alignItems: "center",
                                                gap: "8px",
                                                backgroundColor: mainColor,
                                                color: "white",
                                                border: "none",
                                                borderRadius: "8px",
                                                padding: "8px 12px",
                                            }}
                                            type="button"
                                            onClick={async () => {
                                                downloadAll();
                                            }}
                                            aria-label="Download all CSV"
                                        >
                                            <img
                                                src={exportIcon}
                                                height={18}
                                                width={18}
                                                alt="Download all CSV"
                                                style={{
                                                    mixBlendMode: "normal",
                                                }}
                                            />
                                            {t("Download all")}
                                        </button>
                                    </div>
                                </div>

                                <Box sx={{ height: 500, width: "100%" }}>
                                    <DataGrid
                                        rows={rows.slice(
                                            currentPage * pageSize,
                                            (currentPage + 1) * pageSize
                                        )}
                                        columns={columns.map((column) => ({
                                            ...column,
                                        }))}
                                        pageSize={pageSize}
                                        disableSelectionOnClick
                                        components={{
                                            NoRowsOverlay: NoReportOverlay,
                                            Footer: CustomFooter,
                                        }}
                                        componentsProps={{
                                            footer: {
                                                currentPage,
                                                onPageChange: handlePageChange,
                                                totalPages,
                                                pageSize,
                                                onPageSizeChange:
                                                    handlePageSizeChange,
                                            },
                                        }}
                                    />
                                </Box>
                            </div>
                        </CardBody>
                    </Container>
                </div>
            </div>
            <TransactionsReportingModal
                isModalOpened={isTransactionsModalOpened}
                setIsModalOpened={setIsTransactionsModalOpened}
                data={rowData}
            />
        </React.Fragment>
    );
}

export default SoftaveraTransactionsOverview;
